import { useCallback, useEffect, useState } from "react";
import { Radar, Search, AlertTriangle, X } from "lucide-react";
import { appApi, fmtDate, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { TrackingDetail } from "@/components/TrackingDetail";

const KIND_TABS = [
  { k: "all", l: "All" },
  { k: "b2c", l: "B2C" },
  { k: "b2b", l: "B2B Freight" },
];

export default function Tracker() {
  const [shipments, setShipments] = useState(null);
  const [overdueCount, setOverdueCount] = useState(0);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null); // {kind, id, awb}
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (kind !== "all") params.kind = kind;
    if (q) params.q = q;
    if (overdueOnly) params.overdue = "true";
    appApi.get("tracker/shipments", params)
      .then((d) => { setShipments(d.shipments || []); setError(""); })
      .catch((e) => { setShipments([]); setError(formatApiErrorDetail(e.response?.data?.detail)); });
  }, [kind, q, overdueOnly]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    appApi.get("tracker/overdue").then((d) => setOverdueCount(d.count || 0)).catch(() => {});
  }, []);

  const openDetail = (s) => {
    setOpen(s);
    setDetail(null);
    setDetailErr("");
    appApi.get(`tracker/shipments/${s.kind}/${s.id}`)
      .then((d) => setDetail(d.shipment || d))
      .catch((e) => setDetailErr(formatApiErrorDetail(e.response?.data?.detail)));
  };

  return (
    <div data-testid="tracker-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Shipment Tracker</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Live Tracker</h1>
          <p className="text-sm text-zinc-500 mt-2">Every shipment the tracker watches — courier scans, overdue flags, PODs.</p>
        </div>
        {overdueCount > 0 && (
          <button onClick={() => setOverdueOnly(true)} data-testid="tracker-overdue-banner"
            className="inline-flex items-center gap-2 border-2 border-red-600 bg-red-50 text-red-700 text-xs font-bold uppercase tracking-widest px-4 py-2.5 hover:bg-red-600 hover:text-white transition-colors duration-150">
            <AlertTriangle className="w-4 h-4" /> {overdueCount} overdue
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-0 border-2 border-obsidian w-fit bg-white" data-testid="tracker-kind-tabs">
          {KIND_TABS.map((t) => (
            <button key={t.k} onClick={() => setKind(t.k)} data-testid={`tracker-kind-${t.k}`}
              className={`px-5 py-2.5 text-xs font-mono font-bold uppercase tracking-widest transition-colors duration-150 ${kind === t.k ? "bg-obsidian text-sky-300" : "hover:bg-acid/40"}`}>
              {t.l}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); }} className="flex items-center gap-2 border-2 border-obsidian bg-white px-4 py-2.5 w-72">
          <Search className="w-4 h-4 text-zinc-400 shrink-0" />
          <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search AWB / name / city" data-testid="tracker-search"
            className="text-sm outline-none bg-transparent w-full" />
        </form>
        <button onClick={() => setOverdueOnly((v) => !v)} data-testid="tracker-overdue-toggle"
          className={`text-[11px] font-mono font-bold uppercase tracking-widest px-4 py-2.5 border-2 border-obsidian transition-colors duration-150 ${overdueOnly ? "bg-red-600 text-white" : "bg-white hover:bg-red-50"}`}>
          Overdue only
        </button>
      </div>

      {error && <div className="border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3" data-testid="tracker-error">{error}</div>}

      <div className="border-2 border-obsidian bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]" data-testid="tracker-table">
          <thead>
            <tr className="border-b-2 border-obsidian text-left">
              {["AWB", "Type", "Courier", "Route", "Consignee", "Status", "Booked", "Last scan", "POD"].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(shipments || []).map((s) => (
              <tr key={`${s.kind}-${s.id}`} onClick={() => openDetail(s)}
                className="border-b border-zinc-100 last:border-0 hover:bg-acid/20 transition-colors duration-100 cursor-pointer"
                data-testid={`tracker-row-${s.awb}`}>
                <td className="px-4 py-3 font-mono text-xs font-bold">
                  <span className="flex items-center gap-1.5">
                    {s.awb}
                    {s.overdue && <span className="text-[9px] font-mono font-bold uppercase bg-red-600 text-white px-1.5 py-0.5" data-testid={`tracker-overdue-${s.awb}`}>Overdue</span>}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs uppercase">{s.kind}</td>
                <td className="px-4 py-3 text-zinc-600">{s.courier_name}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{s.pickup_city} → {s.delivery_city}</td>
                <td className="px-4 py-3 text-xs text-zinc-600">{s.delivery_name || "—"}</td>
                <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{fmtDate(s.created_at)}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{s.last_tracker_update_at ? fmtDateTime(s.last_tracker_update_at) : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{s.pods || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {shipments && shipments.length === 0 && <p className="px-5 py-10 text-sm text-zinc-500" data-testid="tracker-empty">No shipments match this view.</p>}
        {!shipments && <p className="px-5 py-10 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading tracker…</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50" data-testid="tracker-detail-drawer">
          <div className="absolute inset-0 bg-obsidian/60" onClick={() => setOpen(null)} />
          <div className="absolute right-0 inset-y-0 w-full max-w-3xl bg-paper overflow-y-auto border-l-4 border-obsidian">
            <div className="sticky top-0 bg-white border-b-2 border-obsidian px-6 py-4 flex items-center justify-between z-10">
              <p className="font-mono text-sm font-bold flex items-center gap-2"><Radar className="w-4 h-4 text-acid" /> {open.awb}</p>
              <button onClick={() => setOpen(null)} aria-label="Close tracker detail" data-testid="tracker-detail-close"
                className="p-2 border-2 border-obsidian hover:bg-acid hover:text-white transition-colors duration-150">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              {!detail && !detailErr && <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Fetching shipment…</p>}
              {detailErr && <p className="border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3">{detailErr}</p>}
              {detail && <TrackingDetail detail={detail} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
